<template>
    <div class="pane-content">
        <!-- Start User Synchronization Overview -->
        <el-row type="flex" class="mt-2">
            <h5 class="section-header">User Synchronization Overview</h5>
        </el-row>
        <el-row type="flex" class="mt-2 mb-1">
            <span>
                This section shows the synchronization statistics between {{ staticName }} and {{ integrationNameWithCasing }}.
                If you see that recent changes in {{ integrationNameWithCasing }} or {{ staticName }} are not reflected, you can <a
                    href="#"
                    @click.prevent="!isLoadingStats && refreshStatistics()"
                    class="refresh-link"
                    :class="{'refresh-link-loading': isLoadingStats}">click here</a> to refresh the statistics.
            </span>
        </el-row>

        <template v-if="isLoadingStats">
            <el-row type="flex" justify="start" class="mt-3 mb-1 stat-cards-container" :gutter="10">
                <el-col :xs="24" :sm="12" :md="8" :lg="7" :xl="5" class="stat-card-col" v-for="i in 3" :key="i">
                    <div class="skeleton-card">
                        <el-skeleton animated>
                            <template slot="template">
                                <div style="padding: 10px; position: relative; min-height: 100px;">
                                    <el-skeleton-item variant="text" style="width: 60%" />

                                    <!-- Loading Spinner -->
                                    <div class="skeleton-spinner-container">
                                        <i class="fa fa-spinner fa-spin"></i>
                                    </div>

                                    <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-top: 30px">
                                        <el-skeleton-item variant="h3" style="width: 50%" />
                                        <el-skeleton-item variant="text" style="width: 30%" />
                                    </div>
                                    <el-skeleton-item variant="text" style="width: 80%" />
                                    <div style="margin-top: 10px">
                                        <el-skeleton-item variant="button" style="width: 100%" />
                                    </div>
                                </div>
                            </template>
                        </el-skeleton>
                    </div>
                </el-col>
            </el-row>
        </template>
        <template v-else>
            <el-row type="flex" justify="start" class="mt-3 mb-1 stat-cards-container" :gutter="10">
                <el-col :xs="24" :sm="12" :md="8" :lg="7" :xl="5" class="stat-card-col">
                    <settings-user-mapping-stat-card
                        :title="(staticName + ' Users').toUpperCase()"
                        :count="statistics.aloware_users"
                        count-label="ACTIVE USERS SYNCHRONIZED"
                        action-button-text="VIEW UNSYNCED USERS"
                        :count-color="isFullSyncAloware ? '#00BF50' : null"
                        :button-color="isFullSyncAloware ? '#00BF50' : null"
                        :hide-action-button="isFullSyncAloware"
                        @action-button-clicked="handleViewUnsynchronizedUsers('aloware')"
                    />
                </el-col>
                <el-col :xs="24" :sm="12" :md="7" :lg="7" :xl="5" class="stat-card-col">
                    <settings-user-mapping-stat-card
                        :title="(integrationNameWithCasing + ' Owners').toUpperCase()"
                        :count="statistics.crm_owners"
                        count-label="OWNERS SYNCHRONIZED"
                        action-button-text="VIEW UNSYNCED OWNERS"
                        :count-color="isFullSyncCrm ? '#00BF50' : null"
                        :button-color="isFullSyncCrm ? '#00BF50' : null"
                        :hide-action-button="isFullSyncCrm"
                        @action-button-clicked="handleViewUnsynchronizedUsers('crm')"
                    />
                </el-col>
                <el-col :xs="24" :sm="12" :md="7" :lg="7" :xl="5" class="stat-card-col">
                    <settings-user-mapping-stat-card
                        title="SYNCHRONIZATION ERRORS"
                        :count="statistics.error_count"
                        count-label="USER MAPPING ISSUES"
                        action-button-icon="fa fa-wrench"
                        action-button-text="FIX ISSUES"
                        :count-color="hasErrors ? '#F56C6C' : '#00BF50'"
                        :button-color="hasErrors ? '#F56C6C' : '#00BF50'"
                        :hide-action-button="!hasErrors"
                        @action-button-clicked="syncUsers"
                    />
                </el-col>
            </el-row>
        </template>
        <div class="p-3 container mb-3">
            <i class="el-icon-warning-outline mr-1"></i>
            <span>
                If there are unsynced users, their call and SMS activity will not be reflected in {{ integrationNameWithCasing }}.
            </span>
        </div>
        <!-- End User Synchronization Overview -->

        <!-- Start User Mapping List -->
        <el-row type="flex" class="mt-2 mb-1">
            <h5 class="section-header">List of Synchronized Users</h5>
        </el-row>

        <!-- Start Sync Users -->
        <el-row type="flex" :gutter="20" align="middle" class="mb-4">
            <el-col :span="15">
                <div class="flex-column">
                    <span>
                        View and manage the connection between {{ staticName }} and {{ integrationNameWithCasing }} owners, <b>linked via email</b>.
                    </span>
                    <br/>
                    <span>
                        The <b>"Sync Users"</b> button will pull owners from {{ integrationNameWithCasing }} that are not yet synchronized and fix any existing mapping issues.
                    </span>
                </div>
            </el-col>
            <el-col :span="10">
                <div class="flex-column justify-center h-100 ml-2">
                    <div>
                        <el-button
                            @click="syncUsers"
                            :disabled="isSyncingUsers"
                            :loading="isSyncingUsers"
                            size="mini"
                            icon="el-icon-refresh"
                        >
                            Sync Users
                        </el-button>
                    </div>
                    <small v-if="model && model.settings && model.settings.last_sync_user" class="mt-2 sync-time-text">
                        Last user sync: <b>{{ model.settings.last_sync_user | fixFullDateUTCRelative }}</b>
                    </small>
                </div>
            </el-col>
        </el-row>
        <!-- End Sync Users -->

        <!-- Search Input -->
        <el-row :gutter="10" class="mb-3" type="flex" align="middle">
            <el-col :span="23">
                <el-input
                    v-model="searchQuery"
                    placeholder="Search by Name, Email, or ID"
                    clearable
                    @keyup.enter.native="handleUserMappingSearch"
                    @clear="handleUserMappingSearch"
                >
                    <el-button
                        slot="append"
                        @click="handleUserMappingSearch"
                        :loading="isSearching"
                        :disabled="isSearching"
                        class="search-button"
                        type="default">
                        <i v-if="!isSearching" class="el-icon-search"></i>
                    </el-button>
                </el-input>
            </el-col>
            <el-col :span="2"></el-col>
        </el-row>
        <!-- End Search Input -->

        <template v-if="isLoadingMappings">
            <el-row class="mt-4 mb-4">
                <el-col :span="24" class="text-center">
                    <div class="empty-state">
                        <i class="fa fa-spinner fa-spin"></i>
                        <p>Loading User Mapping</p>
                    </div>
                </el-col>
            </el-row>
        </template>

        <template v-else>
            <el-row :gutter="20" class="mt-3" v-if="mappedUsers.length > 0">
                <!-- Start Aloware and HubSpot Labels -->
                <el-col :span="11" class="mb-1">
                    <small>{{ staticName }}</small>
                </el-col>
                <el-col :span="1" class="mb-1">
                </el-col>
                <el-col :span="10" class="mb-1">
                    <small>{{ integrationNameWithCasing }}</small>
                </el-col>
                <el-col :span="2" class="mb-1"></el-col>
                <!-- End Aloware and HubSpot Labels -->
            </el-row>

            <el-row v-for="(mappedUser, index) in mappedUsers" :key="`mapping-${index}`" :gutter="10" type="flex" align="middle" class="mb-3">
                <!-- Start Aloware Fields -->
                <el-col :span="12">
                    <div class="user-display-box" :class="{'error-border': mappedUser.has_error}">
                        <div class="user-display-primary">
                            {{ mappedUser.aloware_user.name }} (ID: {{ mappedUser.aloware_user.id || 'None' }})
                        </div>
                        <div class="user-display-secondary">{{ mappedUser.aloware_user.email }}</div>
                    </div>
                </el-col>
                <!-- End Aloware Fields -->

                <el-col :span="1" class="text-center"></el-col>

                <!-- Start CRM Fields -->
                <el-col :span="11">
                    <div class="user-display-box" :class="{'error-border': mappedUser.has_error}">
                        <div class="user-display-primary">{{ mappedUser.crm_owner.name }} (ID: {{ mappedUser.crm_owner.id || 'None' }})</div>
                        <div class="user-display-secondary">{{ mappedUser.crm_owner.email }}</div>
                    </div>
                </el-col>
                <!-- End CRM Fields -->

                <!-- Start Warning Indicator -->
                <el-col :span="2" class="text-center">
                    <el-tooltip
                        v-if="mappedUser.has_error"
                        :content="mappedUser.error_message"
                        placement="top"
                        effect="dark"
                    >
                        <div class="warning-indicator">
                            <i class="fa fa-exclamation-triangle"></i>
                        </div>
                    </el-tooltip>
                </el-col>
                <!-- End Warning Indicator -->
            </el-row>

            <!-- Empty State Message -->
            <el-row v-if="mappedUsers.length === 0" class="mt-4 mb-4">
                <el-col :span="24" class="text-center">
                    <div class="empty-state">
                        <i class="el-icon-info-circle"></i>
                        <p>No User Mappings Found</p>
                        <p v-if="searchQuery" class="text-small">Try adjusting your search criteria</p>
                    </div>
                </el-col>
            </el-row>

            <!-- Start Pagination - only show if there are mappings -->
            <el-row v-if="mappedUsers.length > 0" class="pagination-container mt-4">
                <el-col :span="24" class="text-center">
                    <el-pagination
                        @current-change="handleMappedUsersPageChange"
                        :current-page.sync="mappedUsersPage"
                        :page-size="mappedUsersPageSize"
                        layout="prev, pager, next"
                        :total="mappedUsersTotalItems">
                    </el-pagination>
                </el-col>
            </el-row>
            <!-- End Pagination -->
        </template>
        <!-- End User Mapping List -->

        <!-- Unsynced Users Dialog -->
        <el-dialog
            :visible.sync="unsyncedAlowareDialogVisible"
            width="80%"
            :before-close="handleCloseUnsyncedDialog"
            :append-to-body="true"
            custom-class="dialog-higher-position"
            title="Unsynced Aloware Users"
        >
            <!-- Search bar for unsynced users -->
            <el-input
                v-model="unsyncedUsersSearchQuery"
                placeholder="Search by Name, Email, or ID"
                clearable
                @clear="fetchUnsyncedAlowareUsers"
                @keyup.enter.native="fetchUnsyncedAlowareUsers"
                class="mb-3"
            >
                <el-button
                    slot="append"
                    icon="el-icon-search"
                    @click="fetchUnsyncedAlowareUsers"
                    :loading="isLoadingUnsyncedAloware"
                    :disabled="isLoadingUnsyncedAloware"
                ></el-button>
            </el-input>

            <!-- Loading indicator -->
            <template v-if="isLoadingUnsyncedAloware">
                <el-row class="mt-4 mb-4">
                    <el-col :span="24" class="text-center">
                        <div class="empty-state">
                            <i class="fa fa-spinner fa-spin"></i>
                            <p>Loading Unsynced Users</p>
                        </div>
                    </el-col>
                </el-row>
            </template>

            <!-- Users table -->
            <template v-else>
                <el-table
                    :data="unsyncedAlowareUsers"
                    style="width: 100%"
                    @row-click="openUserTab"
                    :row-class-name="rowClassName"
                    :hover="true"
                    class="unsyncedAlowareTable">
                    <template slot="empty">
                        <!-- Custom empty slot - intentionally left blank -->
                    </template>
                    <el-table-column label="ID" prop="id" width="80"></el-table-column>
                    <el-table-column label="Name" prop="name"></el-table-column>
                    <el-table-column label="Email" prop="email"></el-table-column>
                </el-table>

                <!-- Pagination -->
                <el-row v-if="unsyncedAlowareUsers.length > 0" class="pagination-container mt-4">
                    <el-col :span="24" class="text-center">
                        <el-pagination
                            @current-change="handleUnsyncedAlowarePageChange"
                            :current-page.sync="unsyncedAlowarePage"
                            :page-size="unsyncedAlowarePageSize"
                            layout="prev, pager, next"
                            :total="unsyncedAlowareTotalItems">
                        </el-pagination>
                    </el-col>
                </el-row>
            </template>
        </el-dialog>

        <!-- CRM Owners Dialog -->
        <el-dialog
            :visible.sync="unsyncedCrmDialogVisible"
            width="80%"
            :before-close="handleCloseUnsyncedDialog"
            :append-to-body="true"
            custom-class="dialog-higher-position"
            :title="'Unsynced ' + integrationNameWithCasing + ' Owners'"
        >
            <!-- Search bar for unsynced CRM owners -->
            <el-input
                v-model="unsyncedCrmSearchQuery"
                placeholder="Search by Name, Email, or ID"
                clearable
                @clear="handleUnsyncedCrmSearch"
                @keyup.enter.native="handleUnsyncedCrmSearch"
                class="mb-3"
            >
                <el-button
                    slot="append"
                    icon="el-icon-search"
                    @click="handleUnsyncedCrmSearch"
                    :loading="isLoadingUnsyncedCrm"
                    :disabled="isLoadingUnsyncedCrm"
                ></el-button>
            </el-input>

            <!-- Loading indicator -->
            <template v-if="isLoadingUnsyncedCrm">
                <el-row class="mt-4 mb-4">
                    <el-col :span="24" class="text-center">
                        <div class="empty-state">
                            <i class="fa fa-spinner fa-spin"></i>
                            <p>Loading Unsynced {{ integrationNameWithCasing }} Owners</p>
                        </div>
                    </el-col>
                </el-row>
            </template>

            <!-- Users table -->
            <template v-else>
                <el-table :data="filteredCrmOwners" style="width: 100%">
                    <template slot="empty">
                        <!-- Custom empty slot - intentionally left blank -->
                    </template>
                    <el-table-column label="ID" prop="external_id" width="120"></el-table-column>
                    <el-table-column label="Name" prop="name"></el-table-column>
                    <el-table-column label="Email" prop="email"></el-table-column>
                </el-table>

                <!-- Pagination -->
                <el-row v-if="filteredCrmOwners.length > 0" class="pagination-container mt-4">
                    <el-col :span="24" class="text-center">
                        <el-pagination
                            @current-change="handleUnsyncedCrmPageChange"
                            :current-page.sync="unsyncedCrmPage"
                            :page-size="unsyncedCrmPageSize"
                            layout="prev, pager, next"
                            :total="unsyncedCrmTotalItems">
                        </el-pagination>
                    </el-col>
                </el-row>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import SettingsUserMappingStatCard from './settings-user-mapping-stat-card.vue'

export default {
    components: {
        SettingsUserMappingStatCard
    },
    props: {
        integrationName: {
            type: String,
            required: true
        },
        integrationNameWithCasing: {
            type: String,
            required: true
        },
        statics: {
            type: Object,
            required: true
        },
        model: {
            type: Object,
            required: true
        }
    },
    data: () => ({
        isLoadingStats: false,
        isLoadingMappings: false,
        mappedUsers: [],
        crmOwners: [],
        mappedUsersPage: 1,
        mappedUsersPageSize: 15,
        mappedUsersTotalItems: 0,
        mappedUsersLastPage: 1,
        isSyncingUsers: false,
        isSearching: false,
        searchQuery: '',
        statistics: {
            aloware_users: '0/0',
            crm_owners: '0/0',
            error_count: '0/0'
        },
        unsyncedAlowareDialogVisible: false,
        unsyncedCrmDialogVisible: false,
        unsyncedUsersSearchQuery: '',
        unsyncedCrmSearchQuery: '',
        unsyncedAlowareUsers: [],
        unsyncedCrmOwners: [],
        filteredCrmOwners: [],
        isLoadingUnsyncedAloware: false,
        isLoadingUnsyncedCrm: false,
        unsyncedAlowarePage: 1,
        unsyncedAlowarePageSize: 15,
        unsyncedAlowareTotalItems: 0,
        unsyncedAlowareLastPage: 1,
        unsyncedCrmPage: 1,
        unsyncedCrmPageSize: 15,
        unsyncedCrmTotalItems: 0,
        isFullSyncAloware: false,
        isFullSyncCrm: false,
        hasErrors: false,
    }),
    computed: {
        staticName() {
            return this.statics.name || 'Aloware'
        },
    },
    methods: {
        async fetchMappingData() {
            this.isLoadingMappings = true

            try {
                const mappedUsersResponse = await axios.get(`/api/v1/integrations/user-mappings/${this.integrationName}/mappings`, {
                    params: {
                        page: this.mappedUsersPage,
                        per_page: this.mappedUsersPageSize,
                        query: this.searchQuery
                    }
                })

                this.mappedUsers = mappedUsersResponse.data.data

                const pagination = mappedUsersResponse.data.pagination
                this.mappedUsersTotalItems = pagination.total
                this.mappedUsersLastPage = pagination.last_page
            } catch (error) {
                this.$notify({
                    title: 'Integration Error',
                    message: 'Failed to load user mapping data. Please try again.',
                    type: 'error'
                })
            } finally {
                this.isLoadingMappings = false
            }
        },

        async fetchStatisticsData(fresh = false) {
            this.isLoadingStats = true

            try {
                // Get statistics separately
                const statisticsResponse = await axios.get(`/api/v1/integrations/user-mappings/${this.integrationName}/statistics`, {
                    params: { fresh }
                })
                const stats = statisticsResponse.data.data

                // Set statistics for the stat cards
                this.statistics.crm_owners = `${stats.total_crm_owners_mapped}/${stats.total_crm_owners}`
                this.statistics.error_count = `${stats.total_error_mappings}/${stats.total_mappings}`
                this.statistics.aloware_users = `${stats.total_active_users_mapped}/${stats.total_active_users}`

                this.isFullSyncAloware = stats.total_active_users_mapped === stats.total_active_users
                this.isFullSyncCrm = stats.total_crm_owners_mapped === stats.total_crm_owners
                this.hasErrors = stats.total_error_mappings > 0
            } catch (error) {
                this.$notify({
                    title: 'Integration Error',
                    message: 'Failed to load statistics data. Please try again.',
                    type: 'error'
                })
            } finally {
                this.isLoadingStats = false
            }
        },
        handleViewUnsynchronizedUsers(type = 'aloware') {
            // Save the current state of the main list
            const savedMappedUsers = [...this.mappedUsers]
            const savedTotalItems = this.mappedUsersTotalItems
            const savedLastPage = this.mappedUsersLastPage
            const savedSearchQuery = this.searchQuery
            const savedPage = this.mappedUsersPage

            // Show the appropriate dialog
            if (type === 'crm') {
                this.unsyncedCrmDialogVisible = true
                this.fetchUnsyncedCrmOwners()
            } else {
                this.unsyncedAlowareDialogVisible = true
                this.fetchUnsyncedAlowareUsers()
            }

            // Store the saved state
            this.$options.savedState = {
                mappedUsers: savedMappedUsers,
                totalItems: savedTotalItems,
                lastPage: savedLastPage,
                searchQuery: savedSearchQuery,
                page: savedPage
            }
        },
        async fetchUnsyncedAlowareUsers() {
            try {
                this.isLoadingUnsyncedAloware = true

                const response = await axios.get(`/api/v1/integrations/user-mappings/${this.integrationName}/unsynced/aloware-users`, {
                    params: {
                        page: this.unsyncedAlowarePage,
                        per_page: this.unsyncedAlowarePageSize,
                        query: this.unsyncedUsersSearchQuery
                    }
                })

                // Store the results
                this.unsyncedAlowareUsers = response.data.data || []

                // Store pagination information if available
                if (response.data.pagination) {
                    const pagination = response.data.pagination
                    this.unsyncedAlowareTotalItems = pagination.total || 0
                    this.unsyncedAlowareLastPage = pagination.last_page || 1
                } else {
                    // If pagination is not provided, use the length of data array
                    this.unsyncedAlowareTotalItems = this.unsyncedAlowareUsers.length
                    this.unsyncedAlowareLastPage = 1
                }
            } catch (error) {
                console.error('Error fetching unsynced Aloware users:', error)
                if (error.response) {
                    console.error('Response data:', error.response.data)
                    console.error('Response status:', error.response.status)
                }

                this.$notify({
                    title: 'API Error',
                    message: 'Failed to load unsynced Aloware users. Please check the API endpoint.',
                    type: 'error'
                })

                // Initialize empty arrays on error
                this.unsyncedAlowareUsers = []
                this.unsyncedAlowareTotalItems = 0
                this.unsyncedAlowareLastPage = 1
            } finally {
                this.isLoadingUnsyncedAloware = false
            }
        },
        async fetchUnsyncedCrmOwners() {
            try {
                this.isLoadingUnsyncedCrm = true

                const response = await axios.get(`/api/v1/integrations/user-mappings/${this.integrationName}/unsynced/crm-owners`)

                // Store the results
                this.unsyncedCrmOwners = response.data.data || []

                // Apply initial filtering and pagination
                this.filterAndPaginateCrmOwners()
            } catch (error) {
                console.error('Error fetching unsynced CRM owners:', error)
                if (error.response) {
                    console.error('Response data:', error.response.data)
                    console.error('Response status:', error.response.status)
                }

                this.$notify({
                    title: 'Error',
                    message: 'Failed to load unsynced CRM owners. Please try again.',
                    type: 'error'
                })

                // Initialize empty arrays on error
                this.unsyncedCrmOwners = []
                this.filteredCrmOwners = []
                this.unsyncedCrmTotalItems = 0
            } finally {
                this.isLoadingUnsyncedCrm = false
            }
        },

        filterAndPaginateCrmOwners() {
            // First filter based on search query
            let filtered = this.unsyncedCrmOwners;

            if (this.unsyncedCrmSearchQuery) {
                const searchQuery = this.unsyncedCrmSearchQuery.toLowerCase();
                filtered = this.unsyncedCrmOwners.filter(owner => {
                    return owner.external_id?.toString().toLowerCase().includes(searchQuery) ||
                           owner.name?.toLowerCase().includes(searchQuery) ||
                           owner.email?.toLowerCase().includes(searchQuery);
                });
            }

            // Set total items for pagination
            this.unsyncedCrmTotalItems = filtered.length;

            // Then paginate
            const startIndex = (this.unsyncedCrmPage - 1) * this.unsyncedCrmPageSize;
            const endIndex = startIndex + this.unsyncedCrmPageSize;
            this.filteredCrmOwners = filtered.slice(startIndex, endIndex);
        },

        handleUnsyncedCrmSearch() {
            this.unsyncedCrmPage = 1
            this.filterAndPaginateCrmOwners()
        },

        handleUnsyncedCrmPageChange(newPage) {
            this.unsyncedCrmPage = newPage
            this.filterAndPaginateCrmOwners()
        },

        handleUnsyncedAlowarePageChange(newPage) {
            this.unsyncedAlowarePage = newPage
            this.fetchUnsyncedAlowareUsers()
        },

        handleMappedUsersPageChange(newPage) {
            this.mappedUsersPage = newPage
            this.fetchMappingData()
        },

        async syncUsers() {
            this.isSyncingUsers = true

            try {
                await axios.post(`/api/v1/integration/${this.integrationName}/pull-users`)

                this.$notify({
                    offset: 95,
                    title: this.integrationNameWithCasing,
                    message: `Users are being synced with ${this.integrationNameWithCasing}, please wait for a few minutes for this process to finish. You will receive an email when we finish processing your request.`,
                    type: 'success'
                })
            } catch (error) {
                this.$root.handleErrors(error.response)
            } finally {
                this.isSyncingUsers = false
            }
        },

        async handleUserMappingSearch() {
            this.isSearching = true
            this.mappedUsersPage = 1

            // Clear any saved state to avoid confusion (this is likely how Element UI's dialogs work)
            if (this.$options.savedState) {
                this.$options.savedState = null
            }

            try {
                const response = await axios.get(`/api/v1/integrations/user-mappings/${this.integrationName}/mappings`, {
                    params: {
                        page: this.mappedUsersPage,
                        per_page: this.mappedUsersPageSize,
                        query: this.searchQuery
                    }
                })

                if (response.data && Array.isArray(response.data.data)) {
                    // Clear and set with a small delay to ensure reactivity
                    this.mappedUsers = []
                    setTimeout(() => {
                        this.mappedUsers = [...response.data.data]
                    }, 100)

                    if (response.data.pagination) {
                        this.mappedUsersTotalItems = response.data.pagination.total
                        this.mappedUsersLastPage = response.data.pagination.last_page
                    }
                } else {
                    this.mappedUsers = []
                    this.mappedUsersTotalItems = 0
                    this.mappedUsersLastPage = 1
                }
            } catch (error) {
                console.error('Error during searching:', error)
                this.mappedUsers = []
                this.mappedUsersTotalItems = 0
                this.mappedUsersLastPage = 1
            } finally {
                this.isSearching = false
            }
        },

        handleCloseUnsyncedDialog() {
            this.unsyncedAlowareDialogVisible = false
            this.unsyncedCrmDialogVisible = false

            // Reset dialog pagination and search
            this.unsyncedAlowarePage = 1
            this.unsyncedCrmPage = 1
            this.unsyncedUsersSearchQuery = ''
            this.unsyncedCrmSearchQuery = ''

            // Restore the main list state from before opening the dialog
            if (this.$options.savedState) {
                this.mappedUsers = [...this.$options.savedState.mappedUsers]
                this.mappedUsersTotalItems = this.$options.savedState.totalItems
                this.mappedUsersLastPage = this.$options.savedState.lastPage
                this.searchQuery = this.$options.savedState.searchQuery
                this.mappedUsersPage = this.$options.savedState.page

                // Clear the saved state
                this.$options.savedState = null
            } else {
                // Fallback if no saved state exists
                this.fetchMappingData()
            }
        },

        syncAllUnsyncedUsers() {
            // Since we no longer have tabs, we need to check which dialog is visible
            if (this.unsyncedAlowareDialogVisible) {
                const count = this.unsyncedAlowareUsers.length
                this.unsyncedAlowareUsers = []

                this.$notify({
                    title: 'Success',
                    message: `${count} Aloware users have been synced.`,
                    type: 'success'
                })
            } else if (this.unsyncedCrmDialogVisible) {
                const count = this.unsyncedCrmOwners.length
                this.unsyncedCrmOwners = []

                this.$notify({
                    title: 'Success',
                    message: `${count} CRM owners have been synced.`,
                    type: 'success'
                })
            }
        },

        openUserTab(row) {
            // Open user details in a new tab with the correct URL format
            const baseUrl = window.location.origin
            window.open(`${baseUrl}/users/dialog/${row.id}`, '_blank')
        },

        /**
         * Set row class names for table rows
         */
        rowClassName() {
            return 'row-hover-green el-table-row-custom'
        },
        async refreshStatistics() {
            if (this.isLoadingStats) return
            await this.fetchStatisticsData(true)
        },
    },
    async created() {
        this.fetchStatisticsData()
        this.fetchMappingData()
    }
}
</script>
<style scoped>
.section-header {
    margin: 0 12px 0 0;
    font-size: 20px;
}

.pane-content {
    display: flex;
    flex-direction: column;
    padding-left: 8px;
    padding-right: 8px;
}

.stat-cards-container {
    flex-wrap: wrap;
    margin-bottom: 20px;
}

.stat-card-col {
    display: flex;
    justify-content: flex-start;
    margin-bottom: 20px;
}

@media (max-width: 768px) {
    .stat-card-col {
        padding-left: 0;
    }
}

/* User display boxes */
.user-display-box {
    display: flex;
    flex-direction: column;
    background-color: #f5f7fa;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 10px 12px;
    width: 100%;
    min-height: 60px;
    justify-content: center;
}

.user-display-primary {
    font-weight: 600;
    font-size: 14px;
    color: #333;
    margin-bottom: 5px;
    line-height: 1.4;
}

.user-display-secondary {
    font-size: 13px;
    color: #606266;
    line-height: 1.3;
}

.user-display-id {
    font-size: 12px;
    color: #909399;
    margin-top: 2px;
}

.user-option {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 8px 0;
}

.user-details {
    display: flex;
    flex-direction: column;
    line-height: 1.3;
}

.small-email {
    font-size: 12px;
    color: #8c8c8c;
    margin-top: 1px;
}

.w-100 {
    width: 100%;
}

.warning-indicator {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    color: #F56C6C;
    font-size: 16px;
    cursor: help;
}

.error-border {
    border-color: #F56C6C;
}

.pagination-container {
    margin-top: 20px;
}

/* Flex utilities */
.flex-column {
    display: flex;
    flex-direction: column;
}

.flex-row {
    display: flex;
    flex-direction: row;
}

.justify-center {
    justify-content: center;
}

.align-center {
    align-items: center;
}

.h-100 {
    height: 100%;
}

.section-subheader {
    font-size: 16px;
}

.ml-1 {
    margin-left: 4px;
}

.mt-2 {
    margin-top: 8px;
}

.sync-time-text {
    font-size: 12px;
    color: #606266;
}

/* Style the pagination buttons */
::v-deep .el-pagination .el-pager li.active {
    color: #00BF50;
    font-weight: bold;
}

::v-deep .el-pagination .el-pager li:hover {
    color: #00BF50;
}

::v-deep .el-pagination button:hover {
    color: #00BF50;
}

/* Add global styles to increase the height of el-select dropdown items */
::v-deep .el-select-dropdown__item {
    padding: 10px 20px;
    height: auto;
}

/* Empty state styling */
.empty-state {
    padding: 30px 0;
    text-align: center;
    color: #909399;
}

.empty-state i {
    font-size: 48px;
    margin-bottom: 15px;
}

.empty-state p {
    font-size: 16px;
    margin: 0 0 5px;
}

.empty-state .text-small {
    font-size: 14px;
    color: #C0C4CC;
}

/* Skeleton styling */
.skeleton-card {
    background-color: #f5f7fa;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    width: 100%;
    min-width: 150px;
    max-width: 380px;
    min-height: 120px;
    position: relative;
}

.skeleton-spinner-container {
    position: absolute;
    top: 20px;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
}

.skeleton-spinner-container i {
    font-size: 16px;
    color: #909399;
}

.dialog-nav-footer {
    display: flex;
    justify-content: center;
    margin-top: 20px;
}

/* Custom row hover style for tables */
.row-hover-green:hover {
    cursor: pointer !important;
    background-color: rgba(0, 191, 80, 0.1) !important;
    text-decoration: underline !important;
}

.row-hover-green:hover td {
    color: #00BF50 !important;
}

/* Target only Aloware users table rows, not CRM Owners rows */
::v-deep .unsyncedAlowareTable tbody tr:hover {
    cursor: pointer !important;
}

::v-deep .unsyncedAlowareTable tbody tr:hover td {
    background-color: rgba(0, 191, 80, 0.1) !important;
    color: #00BF50 !important;
    text-decoration: underline !important;
}

::v-deep .el-table__row.el-table-row-custom:hover > td {
    background-color: rgba(0, 191, 80, 0.1) !important;
}

/* Dialog positioning */
::v-deep .dialog-higher-position {
    margin-top: 5vh !important;
}

.refresh-link {
    color: #00BF50;
    text-decoration: none;
    font-weight: 500;
}

.refresh-link:hover {
    text-decoration: underline;
}

.refresh-link-loading {
    color: #85e0ad;
    cursor: wait;
    pointer-events: none;
}

.refresh-link-loading:hover {
    text-decoration: none;
}

.container {
    border: 1px solid grey;
    border-radius: 5px;
    width: 100%;
    max-width: 100%;
}
</style>
